import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type EventType =
  | 'tournament-crib'
  | 'show'
  | 'tasting'
  | 'special'
  | 'other';

export type EventStatus = 'draft' | 'published' | 'cancelled';

export interface BarEvent {
  id: string;
  title: string;
  description: string | null;
  event_type: EventType;
  starts_at: string;
  ends_at: string | null;
  location: string;
  max_participants: number | null;
  price_cents: number | null;
  status: EventStatus;
  cover_image_url: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface EventRegistration {
  id: string;
  event_id: string;
  full_name: string;
  phone: string;
  email: string;
  notes: string | null;
  marketing_consent: boolean;
  registered_at: string;
  user_id: string | null;
}

const EVENTS_KEY = ['events'] as const;
const REGISTRATIONS_KEY = ['event-registrations'] as const;

// ─── PUBLIC : événements visibles (status=published, à venir) ────
export function usePublishedEvents() {
  return useQuery({
    queryKey: [...EVENTS_KEY, 'published'],
    queryFn: async (): Promise<BarEvent[]> => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('status', 'published')
        .gte('starts_at', new Date().toISOString())
        .order('starts_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as BarEvent[];
    },
  });
}

// ─── ADMIN : tous les événements (y compris drafts + passés) ───
export function useAllEvents() {
  return useQuery({
    queryKey: [...EVENTS_KEY, 'all'],
    queryFn: async (): Promise<BarEvent[]> => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('starts_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as BarEvent[];
    },
  });
}

export function useEvent(id: string | null) {
  return useQuery({
    queryKey: [...EVENTS_KEY, id],
    enabled: !!id,
    queryFn: async (): Promise<BarEvent | null> => {
      // maybeSingle() retourne `data: null` si zéro résultat (au lieu de
      // throw avec PGRST116). Plus safe quand un event a été supprimé
      // pendant que l'utilisateur avait la liste en cache.
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', id!)
        .maybeSingle();
      if (error) throw error;
      return (data as BarEvent | null) ?? null;
    },
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Omit<BarEvent, 'id' | 'created_at' | 'updated_at' | 'created_by'>
    ) => {
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('events')
        .insert({
          ...input,
          created_by: userData.user?.id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as BarEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<Omit<BarEvent, 'id' | 'created_at' | 'updated_at' | 'created_by'>>;
    }) => {
      const { data, error } = await supabase
        .from('events')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as BarEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
    },
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('events').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
      queryClient.invalidateQueries({ queryKey: REGISTRATIONS_KEY });
    },
  });
}

// ─── INSCRIPTIONS ────────────────────────────────────────────
export function useEventRegistrations(eventId: string | null) {
  return useQuery({
    queryKey: [...REGISTRATIONS_KEY, eventId ?? 'none'],
    enabled: !!eventId,
    queryFn: async (): Promise<EventRegistration[]> => {
      const { data, error } = await supabase
        .from('event_registrations')
        .select('*')
        .eq('event_id', eventId!)
        .order('registered_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as EventRegistration[];
    },
  });
}

export function useAllEventRegistrations() {
  return useQuery({
    queryKey: REGISTRATIONS_KEY,
    queryFn: async (): Promise<EventRegistration[]> => {
      const { data, error } = await supabase
        .from('event_registrations')
        .select('*')
        .order('registered_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as EventRegistration[];
    },
  });
}

export interface RegisterToEventInput {
  event_id: string;
  full_name: string;
  phone: string;
  email: string;
  notes?: string | null;
  marketing_consent: boolean;
}

export function useRegisterToEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: RegisterToEventInput) => {
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('event_registrations')
        .insert({
          event_id: input.event_id,
          full_name: input.full_name.trim(),
          phone: input.phone.trim(),
          email: input.email.trim().toLowerCase(),
          notes: input.notes?.trim() || null,
          marketing_consent: input.marketing_consent,
          user_id: userData.user?.id ?? null,
        })
        .select()
        .single();
      if (error) throw error;

      // Best-effort : déclenche l'envoi des emails de confirmation
      // (au client + aux admins). On ne bloque pas la mutation si
      // l'edge function échoue — l'inscription est déjà enregistrée.
      try {
        await supabase.functions.invoke('send-event-registration', {
          body: { registration_id: data.id },
        });
      } catch (e) {
        console.warn('send-event-registration failed:', e);
      }

      return data as EventRegistration;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REGISTRATIONS_KEY });
    },
  });
}

export function useDeleteEventRegistration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('event_registrations')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REGISTRATIONS_KEY });
    },
  });
}

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  'tournament-crib': 'Tournoi de crible',
  show: 'Show',
  tasting: 'Soirée dégustation',
  special: 'Événement spécial',
  other: 'Autre',
};
